import React from "react";
import { cn } from "../lib/utils";

/**
 * ET Mark — clean logo (PNG) with optional vertical divider + "Das Einstufungs-Tool"
 * tagline in the brand orange palette.
 *
 * Used in: header, sidebars, footer, favicon.
 *
 * Native source aspect-ratio is preserved. Caller controls the *height*;
 * width adjusts naturally — no distortion.
 *
 * Props:
 * - size: "sm" | "md" | "lg" — visual height of the mark
 * - inverse: true → light mark on dark background
 * - hideTagline: true → mark only (e.g. for very tight spaces / favicon-style usage)
 */
export default function Logo({
  size = "md",
  inverse = false,
  hideTagline = false,
  className,
}) {
  const cfg = {
    sm: { mark: 22, tag: "text-[9px]", divider: "h-5" },
    md: { mark: 30, tag: "text-[10px]", divider: "h-7" },
    lg: { mark: 44, tag: "text-[12px]", divider: "h-10" },
  }[size] || { mark: 30, tag: "text-[10px]", divider: "h-7" };

  const src = inverse ? "/et-mark-light.png" : "/et-mark-dark.png";

  return (
    <div
      className={cn("inline-flex items-center gap-2.5 select-none", className)}
      data-testid="brand-logo-mark"
    >
      <img
        src={src}
        alt="ET — Das Einstufungs-Tool"
        className="block"
        style={{ height: cfg.mark, width: "auto" }}
        draggable={false}
      />
      {!hideTagline && (
        <>
          <div
            className={cn(
              "hidden sm:block w-[1.5px] rounded-full",
              cfg.divider,
              inverse ? "bg-white/40" : "bg-ink-300"
            )}
            aria-hidden="true"
          />
          <span
            className={cn(
              "hidden sm:inline-block uppercase tracking-widest font-semibold text-brand-500 whitespace-nowrap leading-none",
              cfg.tag
            )}
          >
            Das Einstufungs-Tool
          </span>
        </>
      )}
    </div>
  );
}
